import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, EyeOff, Lock, Eye } from "lucide-react";
import { useState } from "react";
import { adminApi, type ProductModuleRow, type ProductModulesState } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  changes,
  type Draft,
  isOn,
  LAUNCH_PRESET_KEYS,
  preset,
  previewSidebar,
  splitSelling,
  toggle,
} from "./product-modules";

const QUERY_KEY = ["admin", "product-modules", "THERESUME"];

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * "theResume Menu": which menu items theResume users see. Hidden items vanish from theResume's
 * sidebar and dashboard and their pages redirect to the dashboard; nothing is deleted.
 */
export function ProductModules() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApi.listProductModules(),
  });
  const [draft, setDraft] = useState<Draft>({});
  const [confirming, setConfirming] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const save = useMutation({
    mutationFn: (modules: Draft) => adminApi.updateProductModules(modules),
    onSuccess: (state: ProductModulesState) => {
      qc.setQueryData(QUERY_KEY, state);
      setDraft({});
      setConfirming(false);
      setJustSaved(true);
    },
  });

  if (isLoading) return <div>Loading...</div>;
  if (error || !data) {
    return <div className="text-red-600">Couldn't load the menu settings: {String(error)}</div>;
  }

  // Presets, groups and the preview are about the menu; selling switches have their own card.
  const { menu: rows, selling } = splitSelling(data.modules);
  const { showing, hiding } = changes(data.modules, draft);
  const pending = showing.length + hiding.length;
  const canEdit = data.canEdit && !save.isPending;

  const edit = (next: Draft) => {
    setDraft(next);
    setJustSaved(false);
  };

  const groups = rows.reduce<{ name: string; rows: ProductModuleRow[] }[]>((list, row) => {
    const name = row.groupName ?? "Other";
    const group = list.find((g) => g.name === name);
    if (group) group.rows.push(row);
    else list.push({ name, rows: [row] });
    return list;
  }, []);

  return (
    <div className="max-w-6xl space-y-6 pb-24">
      <div>
        <h1 className="text-xl font-semibold">theResume Menu</h1>
        <p className="mt-1 text-sm text-gray-600">
          Choose which menu items theResume users see. A hidden item disappears from the sidebar and
          dashboard, and its pages send users to the dashboard. Nothing is deleted: turning an item
          back on restores it as it was.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          {data.lastChange
            ? `Last changed by ${data.lastChange.by} on ${formatWhen(data.lastChange.at)}.`
            : "Not changed since setup."}
        </p>
      </div>

      {!data.canEdit && (
        <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <Lock className="h-4 w-4" />
          You can view these settings. Changing them needs a Super Admin or Business Admin.
        </div>
      )}

      {justSaved && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-sm border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900"
        >
          <CheckCircle2 className="h-4 w-4" />
          Saved. theResume users see the new menu within about a minute.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase text-gray-500">Quick set</span>
            <Button
              size="sm"
              variant="outline"
              disabled={!canEdit}
              onClick={() => edit(preset(rows, LAUNCH_PRESET_KEYS))}
            >
              Resumes + Practice only
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canEdit}
              onClick={() => edit(preset(rows, "all"))}
            >
              Show everything
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Always shown</CardTitle>
              <CardDescription>These can't be hidden.</CardDescription>
            </CardHeader>
            <CardContent className="divide-y">
              {[
                ["Dashboard", "Home page after sign-in."],
                ["Settings", "Account, plan and usage, from the account menu."],
              ].map(([name, description]) => (
                <div key={name} className="flex items-center justify-between gap-4 py-3">
                  <div>
                    <div className="font-medium">{name}</div>
                    <div className="text-sm text-gray-500">{description}</div>
                  </div>
                  <Badge variant="secondary">Always on</Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          {groups.map((group) => (
            <Card key={group.name}>
              <CardHeader>
                <CardTitle className="text-base">{group.name}</CardTitle>
              </CardHeader>
              <CardContent className="divide-y">
                {group.rows.map((row) => (
                  <div key={row.key} className="flex items-center justify-between gap-4 py-3">
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        {row.name}
                        {row.key in draft && <Badge variant="warning">Unsaved</Badge>}
                      </div>
                      {row.description && (
                        <div className="text-sm text-gray-500">{row.description}</div>
                      )}
                    </div>
                    <Switch
                      checked={isOn(row, draft)}
                      disabled={!canEdit}
                      onCheckedChange={(enabled) => edit(toggle(draft, row, enabled))}
                      aria-label={`Show ${row.name}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {selling.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-base">Selling</CardTitle>
                <CardDescription>
                  Not menu items: what theResume offers to buy. The quick-set buttons above don't
                  change these.
                </CardDescription>
              </CardHeader>
              <CardContent className="divide-y">
                {selling.map((row) => (
                  <div key={row.key} className="flex items-center justify-between gap-4 py-3">
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        {row.name}
                        {row.key in draft && <Badge variant="warning">Unsaved</Badge>}
                      </div>
                      {row.description && (
                        <div className="text-sm text-gray-500">{row.description}</div>
                      )}
                    </div>
                    <Switch
                      checked={isOn(row, draft)}
                      disabled={!canEdit}
                      onCheckedChange={(enabled) => edit(toggle(draft, row, enabled))}
                      aria-label={row.name}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        <SidebarPreview rows={rows} draft={draft} />
      </div>

      {pending > 0 && (
        <div className="fixed inset-x-0 bottom-0 border-t bg-white px-6 py-3 shadow-lg lg:left-64">
          <div className="flex max-w-6xl items-center justify-between gap-4">
            <span className="text-sm">
              {pending} unsaved {pending === 1 ? "change" : "changes"}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => edit({})} disabled={save.isPending}>
                Discard
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={!canEdit}>
                Review and save
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={confirming} onOpenChange={(open) => !save.isPending && setConfirming(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change theResume's menu?</DialogTitle>
            <DialogDescription>
              Takes effect for every theResume user within about a minute.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {hiding.length > 0 && (
              <ChangeList icon={<EyeOff className="h-4 w-4" />} label="Hiding" rows={hiding} />
            )}
            {showing.length > 0 && (
              <ChangeList icon={<Eye className="h-4 w-4" />} label="Showing" rows={showing} />
            )}
            {save.error && <p className="text-red-600">Couldn't save: {String(save.error)}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirming(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
              {save.isPending ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChangeList({
  icon,
  label,
  rows,
}: {
  icon: React.ReactNode;
  label: string;
  rows: ProductModuleRow[];
}) {
  return (
    <div>
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {label}
      </div>
      <ul className="ml-6 mt-1 list-disc text-gray-700">
        {rows.map((row) => (
          <li key={row.key}>{row.name}</li>
        ))}
      </ul>
    </div>
  );
}

/** What a theResume user's sidebar will look like with the switches as they are on screen. */
function SidebarPreview({ rows, draft }: { rows: ProductModuleRow[]; draft: Draft }) {
  const entries = previewSidebar(rows, draft);
  const changedClass = "text-amber-700";

  return (
    <Card className="h-fit lg:sticky lg:top-6">
      <CardHeader>
        <CardTitle className="text-base">What candidates see</CardTitle>
        <CardDescription>
          theResume's sidebar{Object.keys(draft).length > 0 && ", unsaved"}.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul
          aria-label="Sidebar preview"
          className="space-y-1 rounded-sm border bg-gray-50 p-3 text-sm"
        >
          <li className="font-medium">Dashboard</li>
          {entries.map((entry) =>
            entry.kind === "item" ? (
              <li key={entry.name} className={entry.changed ? changedClass : undefined}>
                {entry.name}
              </li>
            ) : (
              <li key={entry.name}>
                {entry.name}
                <ul className="ml-4 mt-1 space-y-1 text-gray-600">
                  {entry.items.map((item) => (
                    <li key={item.name} className={item.changed ? changedClass : undefined}>
                      {item.name}
                    </li>
                  ))}
                </ul>
              </li>
            )
          )}
        </ul>
        <p className="mt-2 text-xs text-gray-500">Settings stays in the account menu.</p>
      </CardContent>
    </Card>
  );
}
